import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Key, Menu } from "lucide-react";
import ThemeSwitcher from "./ThemeSwitcher";
import NotificationBell from "./NotificationBell";
import TimerWidget from "./TimerWidget";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ROLE_LABELS, roleBadgeClass } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Header({ toggleMobileSidebar }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const initials = (user?.name || "U")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleOpen = () => {
    setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setErrors({});
    setOpen(true);
  };

  const changePass = async () => {
    const errs = {};
    if (!form.currentPassword) errs.currentPassword = "Current password is required";
    if (!form.newPassword) {
      errs.newPassword = "New password is required";
    } else if (form.newPassword.length < 6) {
      errs.newPassword = "Password must be at least 6 characters long";
    }
    if (form.newPassword !== form.confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      await api.post("/me/change-password", {
        current_password: form.currentPassword,
        new_password: form.newPassword
      });
      toast.success("Password changed successfully");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <header
      className="h-16 sticky top-0 z-40 bg-background/80 backdrop-blur-md border-b border-border flex items-center justify-between px-4 sm:px-6"
      data-testid="app-header"
    >
      <div className="flex items-center gap-3 animate-in fade-in duration-200">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleMobileSidebar}
          className="md:hidden h-9 w-9 text-muted-foreground hover:text-foreground"
          data-testid="mobile-menu-btn"
          title="Open Menu"
        >
          <Menu className="w-5 h-5" />
        </Button>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <TimerWidget />
        <ThemeSwitcher />
        <NotificationBell />
        <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-border">
          <Avatar className="w-8 h-8">
            <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-medium">{user?.name}</span>
            <Badge variant="outline" className={`text-[10px] w-fit ${roleBadgeClass(user?.role)}`}>
              {ROLE_LABELS[user?.role] || user?.role}
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleOpen}
          data-testid="change-password-btn"
          title="Change Password"
          className="h-9 w-9 text-muted-foreground hover:text-foreground"
        >
          <Key className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={logout}
          data-testid="logout-btn"
          title="Logout"
          className="h-9 w-9"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-1">
                <Label htmlFor="current-pass">Current Password</Label>
                <Input
                  id="current-pass"
                  type="password"
                  value={form.currentPassword}
                  onChange={(e) => { setForm({ ...form, currentPassword: e.target.value }); setErrors(prev => ({ ...prev, currentPassword: "" })); }}
                  data-testid="current-password-input"
                />
                {errors.currentPassword && <span className="text-xs text-red-500 block">{errors.currentPassword}</span>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="new-pass">New Password</Label>
                <Input
                  id="new-pass"
                  type="password"
                  value={form.newPassword}
                  onChange={(e) => { setForm({ ...form, newPassword: e.target.value }); setErrors(prev => ({ ...prev, newPassword: "" })); }}
                  data-testid="new-password-input"
                />
                {errors.newPassword && <span className="text-xs text-red-500 block">{errors.newPassword}</span>}
              </div>
              <div className="space-y-1">
                <Label htmlFor="confirm-pass">Confirm New Password</Label>
                <Input
                  id="confirm-pass"
                  type="password"
                  value={form.confirmPassword}
                  onChange={(e) => { setForm({ ...form, confirmPassword: e.target.value }); setErrors(prev => ({ ...prev, confirmPassword: "" })); }}
                  data-testid="confirm-password-input"
                />
                {errors.confirmPassword && <span className="text-xs text-red-500 block">{errors.confirmPassword}</span>}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={changePass} disabled={saving} data-testid="change-password-save">
                {saving ? "Saving…" : "Save Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </header>
  );
}
